import { defineStore } from "pinia";
import type {
  CreateUserDto,
  CreateUserMutation,
  CreateUserMutationVariables,
  DeleteUserMutation,
  DeleteUserMutationVariables,
  GetAllUsersQuery,
  GetAllUsersQueryVariables,
  GetUserQuery,
  GetUserQueryVariables,
  User,
  UpdateUserDto,
  UpdateUserMutation,
  UpdateUserMutationVariables,
} from '@/types/graphql';
import getAllUsers from "@/graphql/queries/getAllUsers.graphql";
import getUser from "@/graphql/queries/getUser.graphql";
import createUser from "@/graphql/mutations/createUser.graphql";
import updateUser from "@/graphql/mutations/updateUser.graphql";
import deleteUser from "@/graphql/mutations/deleteUser.graphql";
import getRoleAssignableUsers from '@/graphql/queries/getRoleAssignableUsers.graphql';
import me from '@/graphql/queries/me.graphql';

/**
 * Manages the users
 */
export const useUsersStore = defineStore("users", {
  state: () => {
    return {
      users: <Partial<User>[]>[],
      loading: false,
      getAllUsersInFlight: null as Promise<any> | null,
      currentUser: null as User,
    };
  },
  actions: {
    /**
     * Create a new user
     *
     * @param createUserDto the user data to create
     */
    async createUser(createUserDto: CreateUserDto) {
      this.loading = true;

      try {
        const res = await storeGraphqlMutation<
          CreateUserMutation,
          CreateUserMutationVariables
        >(
          {
            mutation: createUser,
            variables: {
              ...createUserDto,
            },
          },
          "Fehler beim Erstellen des Benutzers"
        );
        await this.getAllUsers();

        this.loading = false;
        return res;
      } catch (error) {
        this.loading = false;
        await this.getAllUsers();
        throw error;
      }
    },
    /**
     * Fetch all users
     */
    async getAllUsers() {
      if (this.getAllUsersInFlight) {
        return this.getAllUsersInFlight;
      }

      this.getAllUsersInFlight = (async () => {
        this.loading = true;

        try {
          const res = await storeGraphqlQuery<
            GetAllUsersQuery,
            GetAllUsersQueryVariables>(
            {
              query: getAllUsers,
              variables: {},
            },
            "Fehler beim Laden der Benutzer"
          );

          if (res?.data) {
            this.users = res.data.getAllUsers as User[];
          }
          this.loading = false;
          return res;
        } catch (error) {
          this.loading = false;
          throw error;
        }
      })();

      try {
        return await this.getAllUsersInFlight;
      } finally {
        this.getAllUsersInFlight = null;
      }
    },
    /**
     * Update a user
     *
     * @param updateUserDto the user data to update
     */
    async updateUser(updateUserDto: UpdateUserDto) {
      this.loading = true;

      try {
        const res = await storeGraphqlMutation<
          UpdateUserMutation,
          UpdateUserMutationVariables
        >(
          {
            mutation: updateUser,
            variables: {
              ...updateUserDto,
            },
          },
          "Fehler beim Aktualisieren des Benutzers"
        );
        await this.getAllUsers();
        this.loading = false;
        return res;
      } catch (error) {
        this.loading = false;
        await this.getAllUsers();
        throw error;
      }
    },
    /**
     * Delete a user
     *
     * @param id the user id to delete the user
     */
    async deleteUser(id: number) {
      this.loading = true;

      try {
        const res = await storeGraphqlMutation<
          DeleteUserMutation,
          DeleteUserMutationVariables
        >(
          {
            mutation: deleteUser,
            variables: {
              id,
            },
          },
          "Fehler beim Löschen des Benutzers"
        );
        await this.getAllUsers();

        this.loading = false;
        return res;
      } catch (error) {
        this.loading = false;
        await this.getAllUsers();
        throw error;
      }
    },
    /**
     * Get a single user by ID
     *
     * @param id The ID of the user to fetch
     * @returns Promise with the user data
     */
    async getUser(id: number) {
      this.loading = true;

      try {
        const res = await storeGraphqlQuery<
          GetUserQuery,
          GetUserQueryVariables
        >(
          {
            query: getUser,
            variables: {
              id,
            },
          },
          "Error while loading user"
        );

        this.loading = false;
        return res;
      } catch (error) {
        this.loading = false;
        throw error;
      }
    },
    /**
     * Fetch users assignable to roles
     */
    async getRoleAssignableUsers() {
      this.loading = true;
      try {
        const res = await storeGraphqlQuery(
          {
            query: getRoleAssignableUsers,
            variables: {},
          },
          'Fehler beim Laden der zuweisbaren Benutzer'
        );
        if (res?.data) {
          this.users = res.data.getRoleAssignableUsers as User[];
        }
        this.loading = false;
        return res;
      } catch (error) {
        this.loading = false;
        throw error;
      }
    },

    async getSelf() {
      this.loading = true;
      try {
        const res = await storeGraphqlQuery({
          query: me,
          variables: {}
        }, 'Fehler beim Laden des aktuellen Benutzers');
        this.currentUser = res?.data?.me || null;
      } finally {
        this.loading = false;
      }
    }
  },
});